<script>
  import { getContext } from 'svelte';
  import ButtonLabel from '#lib/ButtonLabel.svelte';
  import Icon from '#lib/Icon.svelte';
  import RollingNumberDisplay from '#lib/RollingNumberDisplay.svelte';
  import t from '#lib/i18n.svelte.js';

  // Confirmation sheet for a booking selection: nothing is booked or cancelled until its button is
  // pressed. It sits at the bottom of the booking column (full width on mobile, docked under the
  // calendar on desktop); the parent must be positioned and clip it.
  let {
    /**
     * What is selected, or null when nothing is:
     * { count, unit, price, sub, sub_warn, mini, resource, groups: [{ label, rows: [{ kind, time, meta }] }],
     *   estimate: { total, note } | null, deadline: { text, warn } | null, billing, cta, peek_cta, working }
     */
    summary,
    /** After a confirmed change: { title, lines, email }; email is null when no e-mail follows */
    receipt = null,
    /** A problem with the selection or with the last confirmation */
    error = null,
    busy = false,
    /** The price belongs to an earlier selection while the current one is being priced */
    stale = false,
    on_commit,
    on_clear,
    on_done,
  } = $props();

  let site_settings = getContext('site_settings');

  const GRIP_HEIGHT = 26;
  const MIN_HEIGHT = 64;

  // The member's choice while something is selected. A new selection always starts collapsed, and
  // it replaces a receipt that is still showing as soon as the gesture starts.
  let chosen = $state('peek');
  let sheet_state = $derived(summary ? chosen : receipt ? 'receipt' : 'hidden');
  $effect(() => {
    if (!summary) chosen = 'peek';
  });

  // The last content stays while the sheet slides out
  let shown = $state(null);
  $effect.pre(() => {
    if (summary) shown = summary;
  });

  let sheet_el = $state();
  let grip_el = $state();
  let peek_height = $state(0);

  // Collapsed positions depend only on the peek row, never on the details below it, so a changing
  // selection cannot move the sheet
  function offset_of(state) {
    const height = sheet_el?.offsetHeight ?? 0;
    if (state === 'peek') return Math.max(0, height - GRIP_HEIGHT - peek_height);
    if (state === 'min') return Math.max(0, height - MIN_HEIGHT);
    return 0;
  }

  let drag = $state(null);

  function drag_start(event) {
    if (sheet_state === 'receipt' || sheet_state === 'hidden') return;
    const base = offset_of(sheet_state);
    drag = { start: event.clientY, base, y: base, last: event.clientY, time: performance.now(), velocity: 0, moved: false, target: event.currentTarget };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function drag_move(event) {
    if (!drag) return;
    const dy = event.clientY - drag.start;
    if (Math.abs(dy) > 4) drag.moved = true;
    let y = drag.base + dy;
    if (y < 0) y /= 4; // resistance past the top
    drag.y = Math.min(y, offset_of('min') + 10);
    const now = performance.now();
    drag.velocity = (event.clientY - drag.last) / Math.max(1, now - drag.time);
    drag.last = event.clientY;
    drag.time = now;
  }

  function drag_end() {
    if (!drag) return;
    const { moved, y, velocity, target } = drag;
    drag = null;
    if (!moved) {
      if (target !== grip_el) chosen = 'peek'; // a tap on the collapsed bar opens it
      return;
    }
    const order = ['expanded', 'peek', 'min'];
    let next = order.reduce((best, state) => (Math.abs(offset_of(state) - y) < Math.abs(offset_of(best) - y) ? state : best), 'expanded');
    if (Math.abs(velocity) > 0.5) {
      next = order[Math.max(0, Math.min(2, order.indexOf(sheet_state) + (velocity > 0 ? 1 : -1)))];
    }
    chosen = next;
  }

  function grip_keydown(event) {
    const order = ['expanded', 'peek', 'min'];
    const index = order.indexOf(sheet_state);
    if (index < 0) return;
    if (event.key === 'ArrowDown') chosen = order[Math.min(2, index + 1)];
    else if (event.key === 'ArrowUp') chosen = order[Math.max(0, index - 1)];
    else if (event.key === 'Enter' || event.key === ' ') chosen = sheet_state === 'expanded' ? 'peek' : 'expanded';
    else return;
    event.preventDefault();
  }
</script>

<section
  class="booking-sheet"
  class:dragging={drag}
  data-state={sheet_state}
  bind:this={sheet_el}
  style:--peek-height="{GRIP_HEIGHT + peek_height}px"
  style:--min-height="{MIN_HEIGHT}px"
  style:--drag-y={drag ? `${drag.y}px` : null}
  aria-label={t('booking_sheet_label')}
  aria-hidden={sheet_state === 'hidden'}
>
  <div
    class="grip"
    bind:this={grip_el}
    role="button"
    tabindex={sheet_state === 'hidden' || sheet_state === 'receipt' ? -1 : 0}
    aria-label={t('booking_sheet_move')}
    onpointerdown={drag_start}
    onpointermove={drag_move}
    onpointerup={drag_end}
    onpointercancel={drag_end}
    onkeydown={grip_keydown}
  ></div>

  {#if sheet_state === 'receipt'}
    <div class="receipt flex-column gap-y g-4 px pbe p-6">
      <span class="check" aria-hidden="true"><Icon name="check" /></span>
      <p class="text-heading3">{receipt.title}</p>
      <div class="flex-column gap-y g-2">
        {#each receipt.lines as line}
          <p class="text-body text-2">{line}</p>
        {/each}
        {#if receipt.email}
          <p class="text-caption text-3">{t('booking_confirmation_email_to', { email: receipt.email })}</p>
        {/if}
      </div>
      <button class="full-width" onclick={on_done}>
        <ButtonLabel variant="brand subtle-outlined" class="full-width">{t('booking_done')}</ButtonLabel>
      </button>
    </div>
  {:else if shown}
    <button
      class="mini flex-row items-center gap-x g-4 px p-6"
      onpointerdown={drag_start}
      onpointermove={drag_move}
      onpointerup={drag_end}
      onpointercancel={drag_end}
      onclick={(event) => {
        // Enter and Space arrive as a click without a pointer (detail 0); taps are handled by drag_end
        if (event.detail === 0) chosen = 'peek';
      }}
      tabindex={sheet_state === 'min' ? 0 : -1}
    >
      <span class="badge"><RollingNumberDisplay number={shown.count} class="text-caption" /></span>
      <span class="text-body mini-label">{shown.mini}</span>
      <Icon name="arrow-up" />
    </button>

    <div class="body px pbe p-6" inert={sheet_state === 'min' || sheet_state === 'hidden'}>
      <div class="peek" bind:clientHeight={peek_height}>
        <div class="peek-text">
          <div class="peek-title text-body">
            <RollingNumberDisplay number={shown.count} class="text-body" />
            <span>{shown.unit}</span>
            {#if shown.price}
              <span aria-hidden="true">·</span>
              <span class="price" class:stale><RollingNumberDisplay number={shown.price} class="text-body" /></span>
            {/if}
          </div>
          <p class="peek-sub text-caption" class:warn={error || shown.sub_warn}>{error ?? shown.sub}</p>
        </div>
        {#if sheet_state !== 'expanded'}
          <button class="peek-cta" onclick={on_commit} disabled={busy}>
            <ButtonLabel variant="brand {site_settings.primary_control_surface}">{busy ? shown.working : shown.peek_cta}</ButtonLabel>
          </button>
        {/if}
        <button
          class="toggle"
          onclick={() => (chosen = sheet_state === 'expanded' ? 'peek' : 'expanded')}
          aria-label={t('booking_sheet_toggle')}
          aria-expanded={sheet_state === 'expanded'}
        >
          <ButtonLabel variant="brand outlined" start_icon={sheet_state === 'expanded' ? 'arrow-down' : 'arrow-up'} />
        </button>
      </div>

      <!-- Below the visible part unless expanded, so it must not take keyboard focus -->
      <div class="details flex-column gap-y g-6" inert={sheet_state !== 'expanded'}>
        <p class="text-body">{shown.resource}</p>
        {#each shown.groups as group}
          <div class="flex-column gap-y g-3">
            <p class="text-caption text-3">{group.label}</p>
            {#each group.rows as row}
              <div class="row {row.kind}">
                <span class="time text-body"><span class="glyph {row.kind}" aria-hidden="true"></span>{row.time}</span>
                <span class="meta text-caption text-3">{row.meta}</span>
              </div>
            {/each}
          </div>
        {/each}

        {#if shown.estimate}
          <div class="estimate price flex-column gap-y g-2" class:stale>
            <div class="row">
              <span class="text-body">{t('booking_estimate')}</span>
              <span class="text-body">{shown.estimate.total}</span>
            </div>
            <p class="text-caption text-3">{shown.estimate.note}</p>
          </div>
        {/if}

        {#if shown.deadline}
          <p class="text-caption flex-row items-center gap-x g-3" class:warn={shown.deadline.warn}>
            {#if shown.deadline.warn}<Icon name="warning" />{/if}
            <span>{shown.deadline.text}</span>
          </p>
        {/if}
        <p class="text-caption text-3">{shown.billing}</p>

        {#if error}
          <p class="text-caption warn flex-row items-center gap-x g-3"><Icon name="warning" /><span>{error}</span></p>
        {/if}

        <div class="actions">
          <button class="clear" onclick={on_clear} disabled={busy}>
            <ButtonLabel variant="brand subtle-outlined" class="full-width">{t('booking_clear_selection')}</ButtonLabel>
          </button>
          <button class="commit" onclick={on_commit} disabled={busy}>
            <ButtonLabel variant="brand {site_settings.primary_control_surface}" class="full-width">{busy ? shown.working : shown.cta}</ButtonLabel>
          </button>
        </div>
      </div>
    </div>
  {/if}
</section>

<style>
  .booking-sheet {
    position: absolute;
    inset-inline: 0;
    bottom: 0;
    z-index: 4;
    pointer-events: auto;
    max-height: 84%;
    /* On wide columns the sheet keeps a readable width; a booking page can match it to its calendar */
    max-width: var(--booking-sheet-max-width, 560px);
    margin-inline: auto;
    display: flex;
    flex-direction: column;
    background-color: var(--color-default-background);
    border-radius: var(--radius-lg, var(--size-4)) var(--radius-lg, var(--size-4)) 0 0;
    box-shadow: 0 -1px 0 var(--color-default-stroke), var(--shadow-md);
    transform: translateY(var(--drag-y, var(--y)));
    transition: transform 0.36s cubic-bezier(0.2, 0.8, 0.2, 1);
    /* The white continues below the bottom edge, so pulling the sheet up never uncovers the grid */
    &::after {
      content: '';
      position: absolute;
      inset-inline: 0;
      top: calc(100% - 1px);
      height: 100%;
      background-color: var(--color-default-background);
    }
    &.dragging {
      transition: none;
    }
    &[data-state='peek'] {
      --y: calc(100% - var(--peek-height));
    }
    &[data-state='min'] {
      --y: calc(100% - var(--min-height));
    }
    &[data-state='expanded'],
    &[data-state='receipt'] {
      --y: 0px;
    }
    &[data-state='hidden'] {
      --y: calc(100% + 24px);
      visibility: hidden;
      transition: transform 0.36s cubic-bezier(0.2, 0.8, 0.2, 1), visibility 0s 0.36s;
    }
    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  }

  .grip {
    flex: none;
    height: 26px;
    position: relative;
    z-index: 1; /* above the body's shadow room below */
    cursor: grab;
    touch-action: none;
    &::before {
      content: '';
      position: absolute;
      left: 50%;
      top: 10px;
      width: 36px;
      height: 4px;
      border-radius: 2px;
      background: var(--color-default-background-200);
      transform: translateX(-50%);
    }
    &:focus-visible {
      outline: none;
      &::before {
        box-shadow: 0 0 0 2px var(--color-default-foreground);
      }
    }
  }

  .mini {
    display: none;
    height: calc(var(--min-height) - 26px);
    margin-block-start: -8px;
    width: 100%;
    text-align: start;
    touch-action: none;
    .mini-label {
      flex: 1;
      min-width: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  .booking-sheet[data-state='min'] {
    .mini {
      display: flex;
    }
    .body {
      visibility: hidden;
    }
  }
  .badge {
    min-width: var(--size-8, 24px);
    height: var(--size-8, 24px);
    padding-inline: var(--size-2);
    border-radius: var(--round);
    background: var(--color-default-emphasis-background);
    color: var(--color-default-emphasis-foreground, var(--color-default-foreground));
    display: grid;
    place-items: center;
  }

  /* The body scrolls, which clips; the room at the top (tucked under the grip) keeps the peek buttons' shadows */
  .body {
    overflow-y: auto;
    overscroll-behavior: contain;
    min-height: 0;
    padding-block-start: 12px;
    margin-block-start: -12px;
  }

  /* One line each, so the collapsed height never changes with the selection */
  .peek {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto auto;
    align-items: center;
    gap: var(--size-3);
    padding-block-end: var(--size-5);
  }
  .peek-text {
    min-width: 0;
  }
  .peek-title {
    display: flex;
    align-items: baseline;
    gap: 0.3em;
    white-space: nowrap;
    overflow: hidden;
  }
  .peek-sub {
    color: var(--color-default-foreground-500);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .warn {
    color: oklch(from var(--color-warning) calc(l - 0.3) c h);
  }

  .details {
    border-block-start: 1px solid var(--color-default-stroke);
    padding-block-start: var(--size-5);
  }
  .row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--size-4);
    font-variant-numeric: tabular-nums;
    .meta {
      white-space: nowrap;
    }
    &.remove .time {
      text-decoration: line-through;
      text-decoration-thickness: 1.5px;
      color: var(--color-default-foreground-500);
    }
  }
  /* The same dotted beads as in the calendar: lime to book, red to cancel */
  .glyph {
    display: inline-block;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    margin-inline-end: var(--size-3);
    vertical-align: -2px;
    &.add {
      background: color-mix(in oklab, var(--color-default-emphasis-background) 50%, var(--color-default-background));
      border: 2px dotted oklch(from var(--color-default-emphasis-background) calc(l * 0.9) c h);
    }
    &.remove {
      background: color-mix(in oklab, var(--color-destructive) 16%, var(--color-default-background));
      border: 2px dotted color-mix(in oklab, var(--color-destructive) 58%, var(--color-default-background));
    }
  }
  .estimate {
    border-block-start: 1px solid var(--color-default-stroke);
    padding-block-start: var(--size-5);
  }
  /* While a changed selection is priced, the old price fades; a quick answer never shows it */
  .price {
    transition: opacity 0.15s;
    &.stale {
      opacity: 0.4;
      transition-delay: 0.15s;
    }
  }

  /* Side by side while both labels fit; otherwise the confirm button moves on top, both full width */
  .actions {
    display: flex;
    flex-wrap: wrap-reverse;
    gap: var(--size-3);
    .clear {
      flex: 1 0 auto;
    }
    .commit {
      flex: 999 0 auto;
    }
    button {
      min-width: 0;
    }
  }

  .receipt {
    .check {
      width: var(--size-10, 40px);
      height: var(--size-10, 40px);
      border-radius: var(--round);
      display: grid;
      place-items: center;
      background: var(--color-default-emphasis-background);
    }
  }
</style>
